import {
  getSearchQueryHistoryStore,
  SearchQueryHistoryEntry,
} from 'features/knowledgeDocumentViewer/utils/searchQueryHistoryStorage'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

const EMPTY: SearchQueryHistoryEntry[] = []
const NOOP_SUBSCRIBE = () => () => {}
const NOOP_SNAPSHOT = () => EMPTY
const DEFAULT_VISIBLE_COUNT = 20

export interface UseSearchQueryHistoryReturn {
  addQuery: (query: string, contentFilter: string) => void
  getFilteredQueries: (
    prefix: string,
    limit?: number
  ) => SearchQueryHistoryEntry[]
  recentQueries: (limit?: number) => SearchQueryHistoryEntry[]
  clearHistory: () => void
}

export function useSearchQueryHistory(
  siteId?: string
): UseSearchQueryHistoryReturn {
  const { user } = useGeckoPermissions()
  const userId = user?.id

  const store = useMemo(() => {
    if (!userId || !siteId) return null
    return getSearchQueryHistoryStore(userId, siteId)
  }, [userId, siteId])

  const allEntries = useSyncExternalStore(
    store?.subscribe ?? NOOP_SUBSCRIBE,
    store?.getStableSnapshot ?? NOOP_SNAPSHOT
  )

  const addQuery = useCallback(
    (query: string, contentFilter: string) => {
      if (!store) return
      const trimmed = query.trim()
      if (!trimmed) return

      const current = store.read()
      const filtered = current.filter(
        (e) =>
          !(
            e.query.toLowerCase() === trimmed.toLowerCase() &&
            e.contentFilter === contentFilter
          )
      )

      store.write([
        { query: trimmed, contentFilter, timestamp: Date.now() },
        ...filtered,
      ])
    },
    [store]
  )

  const getFilteredQueries = useCallback(
    (
      prefix: string,
      limit: number = DEFAULT_VISIBLE_COUNT
    ): SearchQueryHistoryEntry[] => {
      const lower = prefix.toLowerCase()
      const matches: SearchQueryHistoryEntry[] = []
      for (const entry of allEntries) {
        if (entry.query.toLowerCase().includes(lower)) {
          matches.push(entry)
          if (matches.length >= limit) break
        }
      }
      return matches
    },
    [allEntries]
  )

  const recentQueries = useCallback(
    (limit: number = DEFAULT_VISIBLE_COUNT): SearchQueryHistoryEntry[] => {
      return allEntries.slice(0, limit)
    },
    [allEntries]
  )

  const clearHistory = useCallback(() => {
    store?.clear()
  }, [store])

  return {
    addQuery,
    getFilteredQueries,
    recentQueries,
    clearHistory,
  }
}
